<script lang="ts">
  import type { BuildState } from '../build-state.svelte.js';
  import { applyUrlToBuild } from '../build-url.js';
  import { lang as langState } from '../lang-state.svelte.js';

  interface Props {
    build: BuildState;
    onAfterApply?: (id: string) => void;
  }

  let { build, onAfterApply }: Props = $props();
  let lang = $derived(langState.current);

  interface ResearchBuild {
    id: string;
    name_en: string;
    name_ru: string;
    set: string;
    weapon: string;
    context: 'countdown' | 'raid';
    params: string;
    burstDps_mil: number;
    sustainedDps_mil: number;
    meta_tier: string;
    comment: string;
  }

  let builds = $state<ResearchBuild[]>([]);
  let loadError = $state<string | null>(null);
  let loadedId = $state<string | null>(null);
  let tierFilter = $state<string>('all');
  let contextFilter = $state<string>('all');

  $effect(() => {
    fetch('/data/research-builds.json', { cache: 'no-cache' })
      .then((r) => r.json())
      .then((d) => { builds = d.builds ?? []; })
      .catch((e) => { loadError = String(e); });
  });

  let filtered = $derived(
    builds.filter((b) => {
      if (tierFilter !== 'all' && b.meta_tier !== tierFilter) return false;
      if (contextFilter !== 'all' && b.context !== contextFilter) return false;
      return true;
    }),
  );

  function apply(p: ResearchBuild) {
    build.reset();
    const params = new URLSearchParams(p.params);
    applyUrlToBuild(build, params);
    loadedId = p.id;
    try {
      const newQs = '?' + p.params;
      history.replaceState(null, '', newQs + '#build');
    } catch { /* ignore */ }
    onAfterApply?.(p.id);
  }

  function tierColor(t: string): string {
    if (t === 'S') return 'red';
    if (t === 'A') return 'orange';
    if (t === 'B') return 'blue';
    return 'gray';
  }
</script>

<section class="panel rb">
  <div class="panel-title">
    <span>🔬 {lang === 'ru' ? 'Мета-билды (research artifact)' : 'Meta builds (research artifact)'}</span>
  </div>

  <div class="rb-info">
    {lang === 'ru'
      ? `42 билда из глубокого research'а (Y8S1 Rise Up, апрель 2026). Burst/Sustained DPS — community estimate ±20%. Клик загружает билд в калькулятор для проверки цифр.`
      : `42 builds from deep research (Y8S1 Rise Up, April 2026). Burst/Sustained DPS — community estimate ±20%. Click loads build into calculator for verification.`}
  </div>

  <div class="rb-filters">
    <div class="rb-fgroup">
      <span class="rb-flabel">{lang === 'ru' ? 'Тир:' : 'Tier:'}</span>
      {#each ['all', 'S', 'A', 'B', 'C'] as t (t)}
        <button class="rb-fbtn" class:active={tierFilter === t} onclick={() => (tierFilter = t)}>
          {t === 'all' ? (lang === 'ru' ? 'все' : 'all') : t}
        </button>
      {/each}
    </div>
    <div class="rb-fgroup">
      <span class="rb-flabel">{lang === 'ru' ? 'Контент:' : 'Content:'}</span>
      {#each ['all', 'countdown', 'raid'] as c (c)}
        <button class="rb-fbtn" class:active={contextFilter === c} onclick={() => (contextFilter = c)}>
          {c === 'all' ? (lang === 'ru' ? 'весь' : 'all') : c}
        </button>
      {/each}
    </div>
    <div class="rb-count">{filtered.length} / {builds.length}</div>
  </div>

  {#if loadError}
    <div class="rb-error">{loadError}</div>
  {/if}

  <div class="rb-grid">
    {#each filtered as p (p.id)}
      <button
        class="rb-btn"
        data-color={tierColor(p.meta_tier)}
        class:active={loadedId === p.id}
        onclick={() => apply(p)}
      >
        <div class="rb-head">
          <span class="rb-tier" data-tier={p.meta_tier}>{p.meta_tier}</span>
          <span class="rb-name">{lang === 'ru' ? p.name_ru : p.name_en}</span>
        </div>
        <div class="rb-dps">
          <span class="rb-dps-cell">
            <span class="rb-dps-l">{lang === 'ru' ? 'Burst' : 'Burst'}</span>
            <span class="rb-dps-v">{p.burstDps_mil}M</span>
          </span>
          <span class="rb-dps-cell">
            <span class="rb-dps-l">{lang === 'ru' ? 'Sustained' : 'Sustained'}</span>
            <span class="rb-dps-v">{p.sustainedDps_mil}M</span>
          </span>
          <span class="rb-ctx">{p.context}</span>
        </div>
        <div class="rb-comment">{p.comment}</div>
      </button>
    {/each}
  </div>
</section>

<style>
  .rb {
    display: block;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto 12px;
    box-sizing: border-box;
  }
  .rb > * { width: 100%; }
  .rb-info {
    display: block;
    font-size: 12px; color: var(--text-dim);
    padding: 4px 0 10px;
    line-height: 1.5;
    clear: both;
  }
  .rb-filters {
    display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
    margin-bottom: 10px;
    clear: both;
  }
  .rb-fgroup { display: flex; gap: 4px; align-items: center; }
  .rb-flabel { font-size: 10px; color: var(--muted); text-transform: uppercase; letter-spacing: .08em; margin-right: 4px; }
  .rb-fbtn {
    padding: 3px 8px; font-size: 10.5px;
    background: var(--bg-2); border: 1px solid var(--border); border-radius: var(--r-sm);
    cursor: pointer; color: var(--text-dim);
  }
  .rb-fbtn:hover { background: var(--card-2); color: var(--text); }
  .rb-fbtn.active { background: var(--orange); border-color: var(--orange); color: #000; }
  .rb-count { margin-left: auto; font-size: 10px; color: var(--muted); }

  .rb-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
    width: 100%;
    clear: both;
  }
  .rb-btn {
    display: flex; flex-direction: column; gap: 5px;
    padding: 10px 12px;
    background: var(--bg-2);
    border: 1px solid var(--border);
    border-left: 3px solid var(--orange);
    border-radius: var(--r-sm);
    cursor: pointer; text-align: left;
    transition: background .12s, border-color .12s, box-shadow .12s;
  }
  .rb-btn[data-color="red"]    { border-left-color: var(--exotic); }
  .rb-btn[data-color="orange"] { border-left-color: var(--named); }
  .rb-btn[data-color="blue"]   { border-left-color: var(--brand); }
  .rb-btn[data-color="gray"]   { border-left-color: var(--muted); }
  .rb-btn:hover { background: var(--card-2); border-color: var(--border-hi); }
  .rb-btn.active { background: rgba(254,175,16,.08); border-color: var(--orange); box-shadow: var(--glow-amber); }

  .rb-head { display: flex; align-items: center; gap: 8px; }
  .rb-tier {
    display: inline-block; min-width: 18px; text-align: center;
    padding: 1px 5px; font: 700 10px/1.4 var(--f-display);
    background: var(--bg-3); border-radius: 3px; letter-spacing: .04em;
  }
  .rb-tier[data-tier="S"] { background: var(--exotic); color: #000; }
  .rb-tier[data-tier="A"] { background: var(--named); color: #000; }
  .rb-tier[data-tier="B"] { background: var(--brand); color: #000; }
  .rb-tier[data-tier="C"] { background: var(--muted); color: #000; }
  .rb-name { font: 700 11px/1.2 var(--f-display); letter-spacing: .06em; color: var(--text); text-transform: uppercase; }

  .rb-dps { display: flex; gap: 12px; align-items: center; }
  .rb-dps-cell { display: flex; flex-direction: column; }
  .rb-dps-l { font-size: 9px; color: var(--muted); text-transform: uppercase; letter-spacing: .06em; }
  .rb-dps-v { font: 700 12px/1 var(--f-display); color: var(--orange); }
  .rb-ctx {
    margin-left: auto; padding: 1px 6px;
    font-size: 9.5px; text-transform: uppercase; letter-spacing: .06em;
    background: var(--bg-3); border-radius: 3px; color: var(--text-dim);
  }

  .rb-comment { font-size: 10.5px; color: var(--text-dim); line-height: 1.45; }
  .rb-error { padding: 8px; color: var(--red); font-size: 11px; }
</style>
